import React, { useCallback, useEffect, useMemo } from 'react';
import SectionHeader from 'new/elements/SectionHeader/SectionHeader';
import { isEqual, keys, map, orderBy } from 'lodash';
import Constants from '../../../../Constants';
import { userDataCategories } from 'js/constants/connectionCategories';
import { FILE_LIST_SORT_CRITERIA } from 'js/constants/paginationConstants';
import DatePicker from '../../../elements/datepicker/DatePicker';
import { defaultStaticRanges, defineds } from 'js/components/elements/datepicker/defaultRanges';
import { FlowLogsView } from 'js/components/pipelines/list/FlowLogsView';
import FlowRunSummary from '../FlowRunSummary';
import { Stack } from 'new/elements/Stack';
import { RefreshIconButton } from 'new/elements/Button/RefreshIconButton';
import CardsListPageNumbers from 'js/components/elements/cards/CardsListPageNumbers';
import {
  ContentSkeleton,
  FlowInsightsHeader,
  FlowRunCountSummary,
} from 'js/components/data_flows/cards/FlowInsights/components';
import FlowRunLogs from 'js/components/data_flows/cards/FlowRunLogs';
import { useFlowInsights } from 'js/components/data_flows/cards/FlowInsights/hooks/useFlowInsights';
import { ReactComponent as PlayIcon } from 'new/components/icons/fi-rr-play.svg';
import { shouldRenderRunAction } from 'js/components/pipelines/cards/components/ExpandActions/utils/shouldRenderSourceAction';

import './FlowInsights.scss';

type FlowInsightsProps = {
  data: any;
  isPreSelected: boolean;
};

const FlowInsights = (props: FlowInsightsProps) => {
  const { data } = props;
  const { type, status, origin_node_id } = data;

  const {
    actions,
    flowRunSummary,
    isRunningFlowNow,
    isGettingFlowRunSummary,
    connectionCategory,
    isSqlAutomation,
    pagination: { page, onPageChange },
    dateRange: {
      range,
      onRangeChange,
      initialRange,
      initialStartDate,
      normalizeDateRange,
      isDirty,
    },
    logsViewState: { log, onChangeLog },
    expandedState: { expandedIndex, onItemExpanded },
    skeletonState: { showSkeleton, onShowSkeleton },
  } = useFlowInsights(props);

  const { meta, data: summaryData } = flowRunSummary[origin_node_id] || {};

  const numRuns = meta?.totalCount || 0;

  const summaryRunIds = useMemo(
    () => orderBy(keys(summaryData), undefined, ['desc']),
    [summaryData],
  );

  const areRunsEmpty = numRuns === 0 && isEqual(range, initialRange);

  const getApiDateRange = useCallback(
    (dateRange: any) => {
      const { start, end } = normalizeDateRange(
        dateRange.startDate.toISOString(),
        dateRange.endDate.toISOString(),
      );

      return { from: start, to: end };
    },
    [normalizeDateRange],
  );

  const loadFlowRunSummary = (page: number = 1, dateRange: any) => {
    const { from, to } = getApiDateRange(dateRange);

    return actions
      .fetchFlowRunSummary(origin_node_id, {
        page,
        per_page: 20,
        orderby: FILE_LIST_SORT_CRITERIA.LAST_CREATED_DESC.value,
        from,
        to,
      })
      .then(() => onShowSkeleton(false));
  };

  const applyDateRange = (value: any) => {
    actions.clearFlowRunSummary();
    onRangeChange(value);
    onPageChange(1);
    loadFlowRunSummary(1, value);
  };

  const onPageChanged = (newPage: number) => {
    if (!areRunsEmpty) {
      onShowSkeleton(true);
    }

    actions.clearFlowRunSummary();
    loadFlowRunSummary(newPage, range);
  };

  const onRefreshClicked = () => {
    actions.clearFlowRunSummary();

    // reset the state
    onItemExpanded(-1);
    onPageChange(1);

    if (!areRunsEmpty) {
      onShowSkeleton(true);
    }

    loadFlowRunSummary(1, range);
  };

  const onExpanded = useCallback(
    (index: number, isExpanded: boolean) => {
      onItemExpanded(isExpanded ? index : -1);
    },
    [onItemExpanded],
  );

  useEffect(() => {
    if (isSqlAutomation) {
      onShowSkeleton(false);
      return undefined;
    }
    loadFlowRunSummary(page, range);

    return () => {
      actions.clearFlowRunSummary();
    };
  }, [isSqlAutomation]);

  const logsInitialMessage = userDataCategories.includes(connectionCategory)
    ? 'Waiting for data...'
    : undefined;

  const isTyringToDetectNewFlowRuns = useMemo(() => {
    return (
      !showSkeleton && type === 'SOURCE' && areRunsEmpty && status === Constants.SourceStatus.ACTIVE
    );
  }, [showSkeleton, type, areRunsEmpty, status]);

  const isRunNowActionAllowed = useMemo(() => {
    return shouldRenderRunAction(data, connectionCategory);
  }, [data, connectionCategory]);

  const onExploreFlowLogs = () => onChangeLog({ data, runId: null, exploreFlowLogs: true });

  const showEmptyState =
    !isTyringToDetectNewFlowRuns && !isGettingFlowRunSummary && summaryRunIds.length === 0;

  // SQL automations do not have flow runs; always show logs view only
  if (isSqlAutomation) {
    return (
      <div className="tab-content data-source-files-tab flow-insights-tab">
        <FlowInsightsHeader onClick={onExploreFlowLogs} />
        <FlowLogsView dataSource={data} fillHeight />
        {log?.data && (
          <FlowRunLogs
            runId={log?.runId}
            originNodeId={log.data.origin_node_id}
            flowNodeId={log.data.flow_node_id}
            connectorType={log.data.connector_type}
            exploreFlowLogs={log?.exploreFlowLogs}
            createdAt={log?.data?.created_at}
            onClose={() => onChangeLog(null)}
            config={{ omitLogScroller: Boolean(log?.exploreFlowLogs) }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="tab-content data-source-files-tab flow-insights-tab">
      <FlowInsightsHeader onClick={onExploreFlowLogs} />

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        padding="16px"
        paddingBottom="8px"
        borderBottom="1px solid #C8C9D0"
      >
        <FlowRunCountSummary range={range} numRuns={numRuns} isLoading={isGettingFlowRunSummary} />

        <Stack direction="row" alignItems="center" justifyContent="flex-end">
          <CardsListPageNumbers
            currentPage={page}
            totalPages={meta?.pageCount || 1}
            totalItems={meta?.totalCount || 0}
            isLoading={isGettingFlowRunSummary}
            onChange={onPageChanged}
            setCurrentPage={onPageChange}
          />

          <DatePicker
            onSelect={applyDateRange}
            data={range}
            staticRanges={defaultStaticRanges}
            dateFormat="MM/dd/yyyy"
            extraProps={{
              minDate: initialStartDate,
              maxDate: defineds.endOfToday,
            }}
          />

          <RefreshIconButton
            isLoading={isGettingFlowRunSummary}
            onRefreshClicked={onRefreshClicked}
          />
        </Stack>
      </Stack>

      {isTyringToDetectNewFlowRuns && (
        <FlowLogsView
          dataSource={data}
          initialMessage={logsInitialMessage}
          onDataSetFound={onRefreshClicked}
        />
      )}

      {summaryRunIds.length > 0 && (
        <Stack padding="16px" borderTop="1px solid var(--color-greyscale-100)">
          <Stack paddingBottom="4px">
            <SectionHeader
              title="Run History"
              subtitle="Review each run of this flow — status, records processed, errors, data volume, and logs."
            />
          </Stack>

          {map(summaryRunIds, (runId, index) => (
            <div key={runId} style={{ marginBottom: '10px' }}>
              <FlowRunSummary
                data={data}
                index={index}
                runId={runId}
                onClickViewLogs={() => onChangeLog({ data, runId })}
                summary={summaryData[runId]}
                isExpanded={expandedIndex === index}
                onExpanded={onExpanded}
              />
            </div>
          ))}
        </Stack>
      )}

      {showEmptyState && (
        <div className="emptyStateContainer">
          {isDirty ? (
            <>
              <div className="title">No Flow Runs found for the selected date range</div>
              <div className="description">New Flows may take a few minutes to appear.</div>
              <div className="refreshFlowRuns">
                <RefreshIconButton
                  isLoading={isGettingFlowRunSummary}
                  onRefreshClicked={onRefreshClicked}
                />
              </div>
            </>
          ) : (
            <>
              <div className="title">No Flow Runs Found</div>
              <div className="description">
                No flow runs found for this data source. Please try again later.
              </div>
              {isRunNowActionAllowed && (
                <div className="runFlowNowButton">
                  <button
                    className="runFlowNowButton__button"
                    onClick={() => actions.runFlowNow(data.data_source_id)}
                    disabled={isRunningFlowNow}
                  >
                    <PlayIcon /> Run Flow Now
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {log?.data && (
        <FlowRunLogs
          runId={log?.runId}
          originNodeId={log.data.origin_node_id}
          flowNodeId={log.data.flow_node_id}
          connectorType={log.data.connector_type}
          exploreFlowLogs={log?.exploreFlowLogs}
          createdAt={log?.data?.created_at}
          onClose={() => onChangeLog(null)}
          config={{ omitLogScroller: Boolean(log?.exploreFlowLogs) }}
        />
      )}

      {isGettingFlowRunSummary && showSkeleton && <ContentSkeleton numItems={20} />}
    </div>
  );
};

export default FlowInsights;
